import {
  LinkOutlined,
  SearchOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import type { FormInstance } from 'antd';
import { Form, Select } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';

import { MoonIcon } from 'app/components/commons/icons/MoonIcon';
import { SunsetIcon } from 'app/components/commons/icons/SunsetIcon';
import type { EditoActionType, IEditoEventForm } from 'app/typings/edito';

import { DaytimePackRedirectionForm } from './DaytimePackRedirectionForm/DaytimePackRedirectionForm';
import './EditoSectionActionForm.scss';
import { HotelRedirectionForm } from './HotelRedirectionForm/HotelRedirectionForm';
import { LinkRedirectionForm } from './LinkRedirectionForm/LinkRedirectionForm';
import { PageRedirectionForm } from './PageRedirectionForm/PageRedirectionForm';
import { SearchRedirectionForm } from './SearchRedirectionForm/SearchRedirectionForm';

const ActionItem = ({
  title,
  icon,
}: {
  title: string;
  icon?: React.ReactNode;
}) => {
  return (
    <div className="editoSectionActionItemContainer">
      {icon}
      <span className="title">{title}</span>
    </div>
  );
};

type ActionValue = 'empty' | EditoActionType;

const actionComponent = (
  fieldName: string | number,
  form: FormInstance<IEditoEventForm>
): Record<ActionValue, React.ReactNode> => {
  return {
    empty: <></>,
    link: <LinkRedirectionForm fieldName={fieldName} />,
    search: <SearchRedirectionForm fieldName={fieldName} form={form} />,
    package: <PageRedirectionForm fieldName={fieldName} />,
    hotel: <HotelRedirectionForm fieldName={fieldName} />,
    pack: <DaytimePackRedirectionForm fieldName={fieldName} />,
  };
};

type Props = {
  fieldName: string | number;
  form: FormInstance<IEditoEventForm>;
  type: 'sections' | 'endingBlock';
};

const OPTIONS_WITH_PACKAGE: DefaultOptionType[] = [
  {
    value: 'empty',
    label: <ActionItem title="No action" />,
  },
  {
    value: 'search',
    label: <ActionItem title="Search redirection" icon={<SearchOutlined />} />,
  },
  {
    value: 'link',
    label: <ActionItem title="Link redirection" icon={<LinkOutlined />} />,
  },
  {
    value: 'hotel',
    label: <ActionItem title="Hotel page redirection" icon={<MoonIcon />} />,
  },
  {
    value: 'pack',
    label: (
      <ActionItem
        title="Daytime package page redirection"
        icon={<SunsetIcon />}
      />
    ),
  },
  {
    // Legacy action kept for editos created before the hotel/pack actions:
    // still rendered when selected, but cannot be picked anymore.
    value: 'package',
    disabled: true,
    label: (
      <ActionItem
        title="Package page redirection (deprecated)"
        icon={<ShoppingOutlined />}
      />
    ),
  },
];

const OPTIONS: DefaultOptionType[] = [
  {
    value: 'empty',
    label: <ActionItem title="No action" />,
  },
  {
    value: 'search',
    label: <ActionItem title="Search redirection" icon={<SearchOutlined />} />,
  },
  {
    value: 'link',
    label: <ActionItem title="Link redirection" icon={<LinkOutlined />} />,
  },
];

export const EditoSectionActionForm = ({ fieldName, form, type }: Props) => {
  const isEndingBlock = type === 'endingBlock';

  const namePath = isEndingBlock
    ? [type, 'actionType']
    : [type, fieldName, 'actionType'];

  const actionType = Form.useWatch<ActionValue>(namePath, form);

  const options = isEndingBlock ? OPTIONS : OPTIONS_WITH_PACKAGE;

  return (
    <div>
      <Form.Item name={[fieldName, 'actionType']}>
        <Select<ActionValue> options={options} />
      </Form.Item>

      {actionComponent(fieldName, form)[actionType ?? 'empty']}
    </div>
  );
};
